// What a launch would do and cost, as POST /v1/admin/evals/estimate answers it
// (TOM-120, ADR 0248 §5, §8): the estimate against the budget and the daily
// cap, the plan per model and role with its judges and the smallest gain each
// of its rates could show (TOM-537, ADR 0342 §6), the failed reads a long
// run's moments answer with, and every refusal the estimate lists, in plain
// words. The launch page shows it before the admin confirms, and a run's page
// shows a re-grade's (TOM-257): the judging alone, since the replies are stored.

import { agentLabel } from './agents';
import { EvalBadge } from './EvalBits';
import { formatUsd, modelShortName } from './format';
import {
  DETECTABLE_TITLE,
  basisText,
  demoCoverageText,
  detectableText,
  refusalText,
  type EvalEstimateRow,
  type EvalLaunchEstimate,
} from './launch';
import { dailyText } from './progress';
import type { EvalRegradeEstimate } from './regrade';
import '../../styles/admin-evals-launch.css';

/** The smallest gain each rate the row is compared on could show (TOM-537): informational. */
function DetectableLine({ row }: { row: EvalEstimateRow }) {
  const text = detectableText(row.detectable);
  if (text === null) return null;
  return (
    <span
      className="admin-muted eval-sub"
      title={DETECTABLE_TITLE}
      data-testid={`eval-estimate-detectable-${row.role}-${row.model}`}
    >
      Smallest gain it can show: {text}
    </span>
  );
}

function Judges({
  estimate,
  model,
}: {
  estimate: EvalLaunchEstimate | EvalRegradeEstimate;
  model: string;
}) {
  // The plan's reason reads "not graded: …"; the badge already says so.
  const notGraded = estimate.notGraded[model]?.replace(/^not graded:\s*/i, '');
  if (estimate.judges === null) {
    return <span className="admin-muted">Phrase heuristic (no judge)</span>;
  }
  const a = estimate.judges.find((j) => j.model === model);
  if (notGraded !== undefined || a === undefined || a.judge === null) {
    return (
      <EvalBadge tone="bad" title={notGraded ?? 'No eligible judge'} testId="eval-not-graded">
        Not graded{notGraded !== undefined ? `: ${notGraded}` : ''}
      </EvalBadge>
    );
  }
  return (
    <span className="eval-nowrap">
      {modelShortName(a.judge)}
      {a.secondJudge !== null ? ` + ${modelShortName(a.secondJudge)}` : ''}
      {estimate.judgeTwice ? <span className="admin-muted"> · twice</span> : null}
    </span>
  );
}

export function EvalLaunchEstimateView({
  estimate: e,
  stale,
}: {
  estimate: EvalLaunchEstimate | EvalRegradeEstimate;
  stale: boolean;
}) {
  const results = e.rows.reduce((s, r) => s + r.plannedResults, 0);
  // A re-grade's estimate (TOM-257) is the judging alone: its replies are stored.
  const regrade = 'source' in e;
  const split = regrade ? e.source.split : e.split;
  // A launch's METS demo questions, run or left out (TOM-196); a re-grade grades its source's.
  const demoText = 'demo' in e ? demoCoverageText(e.demo.cases, e.demo.included) : null;
  return (
    <div
      className={`eval-estimate${stale ? ' eval-estimate--stale' : ''}`}
      data-testid="eval-estimate"
      aria-busy={stale}
    >
      <dl className="eval-estimate-lead">
        <div>
          <dt>Estimate</dt>
          <dd data-testid="eval-estimate-total">{formatUsd(e.estimate.totalUsd)}</dd>
        </div>
        <div>
          <dt>Budget</dt>
          <dd>{formatUsd(e.budgetUsd)}</dd>
        </div>
        <div>
          <dt>At most</dt>
          <dd data-testid="eval-estimate-ceiling">{formatUsd(e.spendCeilingUsd)}</dd>
        </div>
      </dl>
      <p className="admin-muted eval-sub">
        {regrade
          ? `${formatUsd(e.estimate.judgeUsd)} judges, no model calls.`
          : `${formatUsd(e.estimate.modelUsd)} models · ${formatUsd(e.estimate.judgeUsd)} judges.`}{' '}
        A run stops before a case that could cross its budget, so it never spends more than the
        smaller of the two.
      </p>
      <p className="eval-sub" data-testid="eval-estimate-plan">
        {e.plannedCaseCount} {e.plannedCaseCount === 1 ? 'case' : 'cases'} · {results}{' '}
        {results === 1 ? 'result' : 'results'}
        {regrade ? '' : ` · ${e.shards} ${e.shards === 1 ? 'shard' : 'shards'}`}
        {split !== null ? ` · ${split} split` : ''}
      </p>
      {demoText !== null ? (
        <p className="admin-muted eval-sub" data-testid="eval-estimate-demo">
          {demoText}
        </p>
      ) : null}

      <ul className="eval-estimate-rows" aria-label="Each model's plan and cost">
        {e.estimate.rows.map((r) => (
          <li key={`${r.role}|${r.model}`} data-testid={`eval-estimate-row-${r.role}-${r.model}`}>
            <span className="eval-estimate-row-head">
              <span title={r.model}>
                <code>{modelShortName(r.model)}</code>
                {r.role !== 'chat' ? (
                  <span className="admin-muted"> {agentLabel(r.role)}</span>
                ) : null}
              </span>
              <span className="eval-estimate-row-cost">{formatUsd(r.totalUsd)}</span>
            </span>
            {regrade ? (
              <span className="admin-muted eval-sub">
                {r.results} {r.results === 1 ? 'reply' : 'replies'}, {r.modelTurns} for a judge to
                read · {formatUsd(r.judgeUsd)} judging
              </span>
            ) : (
              <span className="admin-muted eval-sub">
                {r.results} {r.results === 1 ? 'result' : 'results'}, {r.modelTurns} reach the model
                · {formatUsd(r.usdPerTurn)} a turn, {basisText(r.turnBasis, r.historyRunId)} ·{' '}
                {formatUsd(r.judgeUsd)} judging
              </span>
            )}
            <span className="eval-sub">
              Judges: <Judges estimate={e} model={r.model} />
            </span>
            <DetectableLine row={r} />
          </li>
        ))}
      </ul>

      <p className="admin-muted eval-sub" data-testid="eval-estimate-daily">
        {e.daily.capUsd === null ? '' : 'Daily cap: '}
        {dailyText(e.daily)}.
      </p>

      {!regrade && e.failedReads.length > 0 ? (
        <details className="eval-failed-reads" data-testid="eval-failed-reads">
          <summary>
            Failed reads the moments answer with ({e.failedReads.length}{' '}
            {e.failedReads.length === 1 ? 'group' : 'groups'})
          </summary>
          {e.failedReads.map((g, i) => (
            <div key={i} className="eval-failed-read">
              <p className="eval-sub">
                {g.moments.map((m) => (
                  <code key={m}>{m} </code>
                ))}
              </p>
              <ul className="eval-list">
                {g.lines.map((l, j) => (
                  <li key={j}>{l}</li>
                ))}
              </ul>
            </div>
          ))}
        </details>
      ) : null}

      {e.refusals.length > 0 ? (
        <ul
          className="eval-refusals"
          aria-label={regrade ? 'What refuses this re-grade' : 'What refuses this launch'}
        >
          {e.refusals.map((r) => {
            const t = refusalText(r, regrade ? 'regrade' : 'launch');
            return (
              <li key={r.code} className="eval-refusal" data-testid={`eval-refusal-${r.code}`}>
                <strong>{t.title}</strong>
                <span>{t.body}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
